// Following a run: its progress on every change and while it runs, its full state on events and
// at most every FULL_EVERY_MS otherwise. Pulled out of WorkflowV4Page so the old-look workflow
// pages, wired to the same API, follow a run the same way.
import { api } from './api';
import type { Progress, RunState } from './contracts';

/** Calls fn again after it settles when asked while it runs, so bursts of events give few reloads. */
export function coalesce(fn: () => Promise<void>): () => void {
  let running = false;
  let again = false;
  const run = async () => {
    if (running) {
      again = true;
      return;
    }
    running = true;
    do {
      again = false;
      await fn();
    } while (again);
    running = false;
  };
  return () => void run();
}

/** The full run state is large (every prompt the engine sent): calls reload it at most this often; events at once. */
const FULL_EVERY_MS = 5000;
/** How often the progress card reloads while the run is unfinished, between events. */
const PROGRESS_EVERY_MS = 2000;

export type Followed = { p: Progress; at: number };

/**
 * Loads the run's progress on every change and while it runs, and its full state on events and at
 * most every FULL_EVERY_MS otherwise. A finished run is no longer watched: it never changes again
 * (follow-ups and reruns are new runs). `calls` is passed on to the state reads (api.state).
 */
export function followRun(
  id: string,
  setRuns: (f: (r: Record<string, RunState>) => Record<string, RunState>) => void,
  setProgress: (f: (r: Record<string, Followed>) => Record<string, Followed>) => void,
  onError: (e: Error) => void,
  calls?: 'summary',
): () => void {
  let done = false;
  let off: (() => void) | null = null;
  let tick: ReturnType<typeof setInterval> | undefined;
  let later: ReturnType<typeof setTimeout> | undefined;
  const stop = () => {
    done = true;
    clearInterval(tick);
    clearTimeout(later);
    off?.();
    off = null;
  };
  const full = coalesce(() =>
    api.state(id, calls).then((s) => {
      setRuns((r) => ({ ...r, [id]: s }));
      if (s.status !== 'finished') return;
      // One last progress, so the card ends on the run's end.
      small();
      stop();
    }, onError),
  );
  const small = coalesce(() =>
    api.progress(id).then((p) => setProgress((r) => ({ ...r, [id]: { p, at: Date.now() } })), onError),
  );
  let last = 0;
  const loadFull = () => {
    clearTimeout(later);
    later = undefined;
    last = Date.now();
    full();
  };
  loadFull();
  small();
  if (!done) {
    tick = setInterval(small, PROGRESS_EVERY_MS);
    off = api.watch(id, (type) => {
      small();
      if (type === 'event' || type === 'state' || Date.now() - last >= FULL_EVERY_MS) loadFull();
      else later ??= setTimeout(loadFull, FULL_EVERY_MS - (Date.now() - last));
    });
  }
  return stop;
}
